import {call, put, takeLatest} from 'redux-saga/effects'
import {
  SETTING_EMAIL_SUBMIT_FORM,
  SETTING_EMAIL_CHANGE_PAGE,
  SETTING_EMAIL_ON_CANCEL_MANAGE_RECIPIENTS,
  SETTING_EMAIL_ON_CLICK_MANAGE_RECIPIENTS,
  SETTING_EMAIL_ON_CONFIRM_MANAGE_RECIPIENTS,
  SETTING_EMAIL_ON_CLICK_DELETE_ICON,
  SETTING_EMAIL_CANCEL_DELETE_RECIPIENT,
  SETTING_EMAIL_DELETE_RECIPIENT,
  SETTING_EMAIL_PAGE_REQUEST
} from 'actions/setting/emailNotification/constants'
import {
  initialize,
  arrayPush,
  arrayRemoveAll,
  arrayRemove
} from 'redux-form'
import * as apiProfile from 'api/profile'
import * as act from 'actions'
import * as actNotif from 'actions/notifications'
import * as actEmailNotif from 'actions/setting/emailNotification'
import * as apiUpdateSetting from 'api/settings'
import * as mpSettingEmail from 'mixpanel/settings/email'
const EMAIL_NOTIFICATION_FORM_NAME = 'emailNotification'
const SETTING_ALERT_RECIPIENTS_FIELD_NAME = 'settingAlertRecipients'

/**
 * @param {object} setting from profile.business.setting
 */
function * fillForm (setting) {
  let data = {
    dailySalesSummary: setting.daily_sales_summary,
    inventoryAlerts: setting.inventory_alerts,
    productUpdates: setting.product_updates,
    settingAlertRecipients: setting.setting_alert_recipients
  }
  yield put(initialize('emailNotification', data))
}

function includeIsAdded (alertRecipients) {
  let newAlertReceipent = [...alertRecipients]
  newAlertReceipent.forEach((alrt) => { alrt.is_added = true })
  return newAlertReceipent
}

function * pageRequest () {
  mpSettingEmail.settingEmailStart()
  try {
    yield put(act.sendingRequest(true))
    yield put(act.showProgressBar())
    let profileRes = yield call(apiProfile.getProfile)
    let profile = profileRes.data
    yield put(actEmailNotif.setProfile(profile))

    // add is_added so that form won't show the row if users
    // does not press confirm while adding new email recipient
    let newSetting = {...profile.business.setting}
    newSetting.setting_alert_recipients = includeIsAdded(profile.business.setting.setting_alert_recipients)

    yield call(fillForm, newSetting)
  } catch (error) {
    yield put(actNotif.error(error.message))
  } finally {
    yield put(act.sendingRequest(false))
    yield put(act.hideProgressBar())
  }
}

function * submitForm (request) {
  let {payload: {data}} = request
  mpSettingEmail.settingEmailUpdate({settings: data.settings})
  try {
    yield put(act.sendingRequest(true))
    yield put(act.showProgressBar())
    yield call(apiUpdateSetting.putUpdateSetting, data)

    // reinitialize again form to make it not 'dirty'
    let profileRes = yield call(apiProfile.getProfile)
    let profile = profileRes.data
    yield put(actEmailNotif.setProfile(profile))

    // add is_added so that form won't show the row if users
    // does not press confirm while adding new email recipient
    let newSetting = {...profile.business.setting}
    newSetting.setting_alert_recipients = includeIsAdded(profile.business.setting.setting_alert_recipients)

    yield call(fillForm, newSetting)
    yield put(actNotif.success('Setting has been successfully updated'))
  } catch (error) {
    yield put(actNotif.error(error.message))
  } finally {
    yield put(act.sendingRequest(false))
    yield put(act.hideProgressBar())
  }
}

function * changePageFlow (request) {
  let {payload: {location}} = request
  window.location.href = location
}

export function * onCancelManageRecipients (request) {
  const {payload: {previousEmailNotificationForm}} = request
  const formName = EMAIL_NOTIFICATION_FORM_NAME
  const fieldName = SETTING_ALERT_RECIPIENTS_FIELD_NAME

  // set to previous state
  yield put(arrayRemoveAll(formName, fieldName))
  for (let i = 0; i < previousEmailNotificationForm[fieldName].length; i++) {
    let d = previousEmailNotificationForm[fieldName][i]
    yield put(arrayPush(EMAIL_NOTIFICATION_FORM_NAME, SETTING_ALERT_RECIPIENTS_FIELD_NAME, d))
  }
  yield put(actEmailNotif.hideRecipientModal())
  yield put(actEmailNotif.hideSettingAlertRecipientsErrors())
}

export function * onClickManageRecipients (request) {
  const {payload: {previousEmailNotificationForm}} = request
  const formName = EMAIL_NOTIFICATION_FORM_NAME
  const fieldName = SETTING_ALERT_RECIPIENTS_FIELD_NAME

  yield put(actEmailNotif.setPreviousEmailNotificationForm(previousEmailNotificationForm))
  yield put(arrayPush(formName, fieldName, {email: '', is_added: false}))
  yield put(actEmailNotif.showRecipientModal())
  yield put(actEmailNotif.hideSettingAlertRecipientsErrors())
}

export function * onConfirmManageRecipients (request) {
  const {payload: {emailNotificationForm, previousEmailNotificationForm}} = request

  const formName = EMAIL_NOTIFICATION_FORM_NAME
  const fieldName = SETTING_ALERT_RECIPIENTS_FIELD_NAME

  // remove all blank email
  yield put(arrayRemoveAll(formName, fieldName))
  for (let i = 0; i < emailNotificationForm[fieldName].length; i++) {
    let d = emailNotificationForm[fieldName][i]
    if (d.email) {
      if (i < previousEmailNotificationForm.length) {
        yield put(arrayPush(formName, fieldName, d))
      } else {
        for (let j = previousEmailNotificationForm.length; j < emailNotificationForm[fieldName].length; j++) {
          let index = previousEmailNotificationForm.findIndex(x => x.email === d.email)
          if (index < 0 && d.email) {
            yield put(arrayPush(formName, fieldName, {...d, is_added: true}))
            break
          } else {
            let em = emailNotificationForm[fieldName][j]
            let idx = previousEmailNotificationForm.findIndex(x => x.email === em.email)
            if (idx >= 0) {
              yield put(arrayRemove(formName, fieldName, j))
            }
          }
        }
      }
    }
  }

  yield put(actEmailNotif.hideRecipientModal())
}

export function * onClickDeleteIcon (request) {
  let {payload: {deletedRecipient}} = request
  yield put(actEmailNotif.setDeletedRecipient(deletedRecipient))
  yield put(actEmailNotif.showDeleteRecipientModal())
}

export function * cancelDeleteRecipient () {
  yield put(actEmailNotif.setDeletedRecipient(null))
  yield put(actEmailNotif.hideDeleteRecipientModal())
}

export function * deleteRecipient (request) {
  let {payload: {deletedRecipient, emailNotificationForm}} = request
  let {settingAlertRecipients} = emailNotificationForm

  const formName = EMAIL_NOTIFICATION_FORM_NAME
  const fieldName = SETTING_ALERT_RECIPIENTS_FIELD_NAME
  let idx = settingAlertRecipients.indexOf(deletedRecipient)
  yield put(arrayRemove(formName, fieldName, idx))
  yield put(actEmailNotif.hideDeleteRecipientModal())
}

export default function * emailNotificationSaga () {
  yield takeLatest(SETTING_EMAIL_PAGE_REQUEST, pageRequest)
  yield takeLatest(SETTING_EMAIL_SUBMIT_FORM, submitForm)
  yield takeLatest(SETTING_EMAIL_CHANGE_PAGE, changePageFlow)
  yield takeLatest(SETTING_EMAIL_ON_CANCEL_MANAGE_RECIPIENTS, onCancelManageRecipients)
  yield takeLatest(SETTING_EMAIL_ON_CLICK_MANAGE_RECIPIENTS, onClickManageRecipients)
  yield takeLatest(SETTING_EMAIL_ON_CONFIRM_MANAGE_RECIPIENTS, onConfirmManageRecipients)
  yield takeLatest(SETTING_EMAIL_ON_CLICK_DELETE_ICON, onClickDeleteIcon)
  yield takeLatest(SETTING_EMAIL_CANCEL_DELETE_RECIPIENT, cancelDeleteRecipient)
  yield takeLatest(SETTING_EMAIL_DELETE_RECIPIENT, deleteRecipient)
}



// WEBPACK FOOTER //
// ./app/sagas/setting/emailNotification.js